import { useForm } from '@inertiajs/react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface InviteUserSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  teams: { id: number; name: string }[]
}

export function InviteUserSheet({ open, onOpenChange, teams }: InviteUserSheetProps) {
  const { data, setData, post, processing, errors, reset } = useForm({
    invitation: {
      first_name: '',
      last_name: '',
      email: '',
      team_id: '',
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    post('/admin/users/invite', {
      preserveScroll: true,
      onSuccess: () => {
        reset()
        onOpenChange(false)
      },
    })
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[540px] sm:max-w-[540px]">
        <SheetHeader>
          <SheetTitle>Invite User</SheetTitle>
          <SheetDescription>
            Send an invitation to join the organization.
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="space-y-4 p-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="invite_first_name">First Name</Label>
              <Input
                id="invite_first_name"
                value={data.invitation.first_name}
                onChange={(e) => setData('invitation', { ...data.invitation, first_name: e.target.value })}
                placeholder="Fatma"
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="invite_last_name">Last Name</Label>
              <Input
                id="invite_last_name"
                value={data.invitation.last_name}
                onChange={(e) => setData('invitation', { ...data.invitation, last_name: e.target.value })}
                placeholder="Ben Ali"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="invite_email">Email</Label>
            <Input
              id="invite_email"
              type="email"
              value={data.invitation.email}
              onChange={(e) => setData('invitation', { ...data.invitation, email: e.target.value })}
              required
              placeholder="user@example.com"
              autoFocus
            />
            {errors['invitation.email'] && (
              <p className="text-sm text-destructive">{errors['invitation.email']}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="invite_team">Team</Label>
            <Select
              value={data.invitation.team_id}
              onValueChange={(val) => setData('invitation', { ...data.invitation, team_id: val })}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a team" />
              </SelectTrigger>
              <SelectContent>
                {teams.map((t) => (
                  <SelectItem key={t.id} value={t.id.toString()}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors['invitation.team_id'] && (
              <p className="text-sm text-destructive">{errors['invitation.team_id']}</p>
            )}
          </div>

          <Button type="submit" disabled={processing} className="w-full">
            {processing ? 'Sending...' : 'Send Invitation'}
          </Button>
        </form>
      </SheetContent>
    </Sheet>
  )
}
